import { useInfiniteQuery } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';
import { DocumentsService } from 'api';
import { getNormalizedSearch } from 'services/hooks/documents/documentsQuery.utils';
import {
  FIRM_DOCUMENTS_QUERY_KEY,
  FIRM_DOCUMENTS_QUERY_PAGE_SIZE,
  FIRM_DOCUMENTS_QUERY_STALE_TIME_MS,
} from './useFirmDocumentsQuery.constants';
import type { UseFirmDocumentsQueryParams } from './useFirmDocumentsQuery.types';

type FirmDocumentsResponse = Awaited<ReturnType<typeof DocumentsService.apiDocumentsFirmsDocumentsList>>;
type FirmDocumentsUploadedByParam = Parameters<typeof DocumentsService.apiDocumentsFirmsDocumentsList>[8];
type FirmDocumentsFileTypeParam = Parameters<typeof DocumentsService.apiDocumentsFirmsDocumentsList>[9];
type FirmDocumentsSourceParam = Parameters<typeof DocumentsService.apiDocumentsFirmsDocumentsList>[10];
type FirmDocumentsReferenceObjectTypeParam = Parameters<typeof DocumentsService.apiDocumentsFirmsDocumentsList>[11];
type FirmDocumentsOrderingParam = Parameters<typeof DocumentsService.apiDocumentsFirmsDocumentsList>[12];

const FIRM_DOCUMENTS_QUERY_NEXT_PAGE_FALLBACK_BASE_URL = 'http://localhost';

const getFileTypeParam = (fileType?: string | null) => (fileType || undefined) as FirmDocumentsFileTypeParam;
const getUploadedByParam = (uploadedBy?: string | null) => {
  const uploadedById = Number(uploadedBy);

  return (uploadedById > 0 ? uploadedById : undefined) as FirmDocumentsUploadedByParam;
};
const getSourceParam = (source?: string | null) => (source || undefined) as FirmDocumentsSourceParam;
const getReferenceObjectTypeParam = (referenceObjectType?: string | null) => (
  referenceObjectType || undefined
) as FirmDocumentsReferenceObjectTypeParam;
const getOrderingParam = (ordering?: string | null) => (
  ordering || 'company_name'
) as FirmDocumentsOrderingParam;

const getNextFirmDocumentsPageParam = (
  lastPage: FirmDocumentsResponse | undefined,
  allPages: FirmDocumentsResponse[]
) => {
  if (!lastPage?.next) {
    return undefined;
  }

  try {
    const nextPage = Number(
      new URL(lastPage.next, FIRM_DOCUMENTS_QUERY_NEXT_PAGE_FALLBACK_BASE_URL).searchParams.get('page')
    );

    return nextPage > 0 ? nextPage : allPages.length + 1;
  } catch {
    return allPages.length + 1;
  }
};

export const useFirmDocumentsQuery = ({
  companyId = null,
  enabled = true,
  fileType = null,
  firmId,
  firmLevel = false,
  fundId = null,
  measurementDateId = null,
  measurementDate = null,
  ordering = 'company_name',
  referenceObjectType = null,
  search,
  source = null,
  uploadedBy = null,
}: UseFirmDocumentsQueryParams) => {
  const normalizedSearch = getNormalizedSearch(search);
  const normalizedOrdering = getOrderingParam(ordering);

  return useInfiniteQuery<FirmDocumentsResponse, Error, InfiniteData<FirmDocumentsResponse, number>, readonly unknown[], number>({
    enabled: Boolean(firmId) && enabled,
    getNextPageParam: getNextFirmDocumentsPageParam,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      DocumentsService.apiDocumentsFirmsDocumentsList(
        String(firmId),
        Number(pageParam),
        FIRM_DOCUMENTS_QUERY_PAGE_SIZE,
        normalizedSearch,
        companyId ?? undefined,
        measurementDateId ?? undefined,
        measurementDate ?? undefined,
        fundId ?? undefined,
        getUploadedByParam(uploadedBy),
        getFileTypeParam(fileType),
        getSourceParam(source),
        getReferenceObjectTypeParam(referenceObjectType),
        normalizedOrdering,
        firmLevel
      ),
    queryKey: [
      FIRM_DOCUMENTS_QUERY_KEY,
      firmId,
      companyId ?? '',
      measurementDateId ?? '',
      measurementDate ?? '',
      fundId ?? '',
      fileType ?? '',
      source ?? '',
      uploadedBy ?? '',
      referenceObjectType ?? '',
      normalizedOrdering,
      normalizedSearch ?? '',
      firmLevel,
    ],
    staleTime: FIRM_DOCUMENTS_QUERY_STALE_TIME_MS,
  });
};
